"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OtherLine = exports.TableColumnLine = exports.ParagraphItemLine = exports.ArticleLine = exports.SupplProvisionAppdxItemHeadLine = exports.SupplProvisionHeadLine = exports.AppdxItemHeadLine = exports.ArticleGroupHeadLine = exports.TOCHeadLine = exports.BlankLine = exports.LineType = void 0;
const _sentenceChildren_1 = require("../../parser/cst/rules/$sentenceChildren");
const _sentencesArray_1 = require("../../parser/cst/rules/$sentencesArray");
const el_1 = require("../el");
/**
 * LineType: the identifiers that distinguish the different types of `Line`.
 */
var LineType;
(function (LineType) {
    LineType["BNK"] = "BNK";
    LineType["TOC"] = "TOC";
    LineType["ARG"] = "ARG";
    LineType["APP"] = "APP";
    LineType["SPR"] = "SPR";
    LineType["SPA"] = "SPA";
    LineType["ART"] = "ART";
    LineType["PIT"] = "PIT";
    LineType["TBL"] = "TBL";
    LineType["OTH"] = "OTH";
})(LineType || (exports.LineType = LineType = {}));
class BaseLine {
    type;
    range;
    lineEndText;
    constructor(options) {
        this.type = options.type;
        this.range = options.range;
        this.lineEndText = options.lineEndText;
    }
    text() {
        return this.rangeTexts().map(([, t]) => t).join("");
    }
    lineEndTextRange() {
        if (!this.range)
            return null;
        return [this.range[1] - this.lineEndText.length, this.range[1]];
    }
}
class IndentsLine extends BaseLine {
    indentTexts;
    constructor(options) {
        super(options);
        this.indentTexts = options.indentTexts;
    }
    indentRangeTexts() {
        const ret = [];
        let lastEnd = this.range ? this.range[0] : NaN;
        for (let i = 0; i < this.indentTexts.length; i++) {
            ret.push([
                (!isNaN(lastEnd)) ? [lastEnd, lastEnd + this.indentTexts[i].length] : null,
                this.indentTexts[i],
                "Indent",
            ]);
            lastEnd += this.indentTexts[i].length;
        }
        return ret;
    }
    get indentsEndPos() {
        const indentRangeTexts = this.indentRangeTexts();
        const pos = (indentRangeTexts.length > 0
            ? indentRangeTexts[indentRangeTexts.length - 1][0]?.[1]
            : null) ?? (this.range && this.range[0]) ?? null;
        return pos;
    }
}
class WithControlsLine extends IndentsLine {
    controls;
    constructor(options) {
        super(options);
        this.controls = options.controls;
    }
    controlsRangeTexts() {
        const ret = [];
        for (const control of this.controls) {
            ret.push([control.controlRange, control.control, "Control"]);
            ret.push([control.trailingSpaceRange, control.trailingSpace, "ControlTrailingSpace"]);
        }
        return ret;
    }
    get controlsEndPos() {
        const pos = (this.controls.length > 0
            ? this.controls[this.controls.length - 1].trailingSpaceRange?.[1]
            : null) ?? this.indentsEndPos;
        return pos;
    }
}
/* eslint-disable tsdoc/syntax */
/**
 * A blank line with no printable characters. Please see the source code of {@link $blankLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class BlankLine extends BaseLine {
    constructor(options) {
        super({ ...options, type: LineType.BNK });
    }
    rangeTexts() {
        return [[this.lineEndTextRange(), this.lineEndText, "LineEnd"]];
    }
}
exports.BlankLine = BlankLine;
/* eslint-disable tsdoc/syntax */
/**
 * A head line of a TOC (Table Of Contents). Please see the source code of {@link $tocHeadLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class TOCHeadLine extends WithControlsLine {
    title;
    constructor(options) {
        super({ ...options, type: LineType.TOC });
        this.title = options.title;
    }
    get titleRange() {
        if (!this.range)
            return null;
        const lastEnd = ((this.controlsEndPos) ??
            (this.range[0] + this.indentTexts.map(t => t.length).reduce((a, b) => a + b, 0)));
        return [
            lastEnd,
            lastEnd + this.title.length,
        ];
    }
    rangeTexts() {
        const ret = [];
        ret.push(...this.indentRangeTexts());
        ret.push(...this.controlsRangeTexts());
        ret.push([this.titleRange, this.title, "Title"]);
        ret.push([this.lineEndTextRange(), this.lineEndText, "LineEnd"]);
        return ret;
    }
}
exports.TOCHeadLine = TOCHeadLine;
/* eslint-disable tsdoc/syntax */
/**
 * A head line of an article group (e.g. "Chapter"). Please see the source code of {@link $articleGroupHeadLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class ArticleGroupHeadLine extends WithControlsLine {
    mainTag;
    title;
    constructor(options) {
        super({ ...options, type: LineType.ARG });
        this.mainTag = options.mainTag;
        this.title = options.sentenceChildren;
    }
    get titleRange() {
        if (this.title.length > 0) {
            return (0, el_1.rangeOfELs)(this.title);
        }
        else {
            const pos = this.controlsEndPos;
            return (pos !== null) ? [pos, pos] : null;
        }
    }
    rangeTexts() {
        const ret = [];
        ret.push(...this.indentRangeTexts());
        ret.push(...this.controlsRangeTexts());
        ret.push([this.titleRange, (0, _sentenceChildren_1.sentenceChildrenToString)(this.title), "Title"]);
        ret.push([this.lineEndTextRange(), this.lineEndText, "LineEnd"]);
        return ret;
    }
}
exports.ArticleGroupHeadLine = ArticleGroupHeadLine;
/* eslint-disable tsdoc/syntax */
/**
 * A head line of an appended item such as an appended table. Please see the source code of {@link $appdxItemHeadLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class AppdxItemHeadLine extends WithControlsLine {
    mainTag;
    title;
    relatedArticleNum;
    constructor(options) {
        super({ ...options, type: LineType.APP });
        this.mainTag = options.mainTag;
        this.title = options.title;
        this.relatedArticleNum = options.relatedArticleNum;
    }
    get titleRange() {
        if (this.title.length > 0) {
            return (0, el_1.rangeOfELs)(this.title);
        }
        else {
            const pos = this.controlsEndPos;
            return (pos !== null) ? [pos, pos] : null;
        }
    }
    get relatedArticleNumRange() {
        if (this.relatedArticleNum.length > 0) {
            return (0, el_1.rangeOfELs)(this.relatedArticleNum);
        }
        else {
            const base = this.titleRange;
            if (!base)
                return null;
            return [base[1], base[1]];
        }
    }
    rangeTexts() {
        const ret = [];
        ret.push(...this.indentRangeTexts());
        ret.push(...this.controlsRangeTexts());
        ret.push([this.titleRange, (0, _sentenceChildren_1.sentenceChildrenToString)(this.title), "Title"]);
        ret.push([this.relatedArticleNumRange, (0, _sentenceChildren_1.sentenceChildrenToString)(this.relatedArticleNum), "RelatedArticleNum"]);
        ret.push([this.lineEndTextRange(), this.lineEndText, "LineEnd"]);
        return ret;
    }
}
exports.AppdxItemHeadLine = AppdxItemHeadLine;
/* eslint-disable tsdoc/syntax */
/**
 * A head line of a supplementary provision. Please see the source code of {@link $supplProvisionHeadLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class SupplProvisionHeadLine extends WithControlsLine {
    title;
    titleRange;
    openParen;
    amendLawNum;
    closeParen;
    extractText;
    constructor(options) {
        super({ ...options, type: LineType.SPR });
        this.title = options.title;
        this.titleRange = options.titleRange;
        this.openParen = options.openParen;
        this.amendLawNum = options.amendLawNum;
        this.closeParen = options.closeParen;
        this.extractText = options.extractText;
    }
    get openParenRange() {
        const base = this.titleRange;
        if (!base)
            return null;
        return [base[1], base[1] + this.openParen.length];
    }
    get amendLawNumRange() {
        const base = this.openParenRange;
        if (!base)
            return null;
        return [base[1], base[1] + this.amendLawNum.length];
    }
    get closeParenRange() {
        const base = this.amendLawNumRange;
        if (!base)
            return null;
        return [base[1], base[1] + this.closeParen.length];
    }
    get extractTextRange() {
        const base = this.closeParenRange;
        if (!base)
            return null;
        return [base[1], base[1] + this.extractText.length];
    }
    rangeTexts() {
        const ret = [];
        ret.push(...this.indentRangeTexts());
        ret.push(...this.controlsRangeTexts());
        ret.push([this.titleRange, this.title, "Title"]);
        ret.push([this.openParenRange, this.openParen, "OpenParen"]);
        ret.push([this.amendLawNumRange, this.amendLawNum, "AmendLawNum"]);
        ret.push([this.closeParenRange, this.closeParen, "CloseParen"]);
        ret.push([this.extractTextRange, this.extractText, "ExtractText"]);
        ret.push([this.lineEndTextRange(), this.lineEndText, "LineEnd"]);
        return ret;
    }
}
exports.SupplProvisionHeadLine = SupplProvisionHeadLine;
/* eslint-disable tsdoc/syntax */
/**
 * A head line of an appended item in a supplementary provision. Please see the source code of {@link $supplProvisionAppdxItemHeadLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class SupplProvisionAppdxItemHeadLine extends WithControlsLine {
    mainTag;
    title;
    relatedArticleNum;
    constructor(options) {
        super({ ...options, type: LineType.SPA });
        this.mainTag = options.mainTag;
        this.title = options.title;
        this.relatedArticleNum = options.relatedArticleNum;
    }
    get titleRange() {
        if (this.title.length > 0) {
            return (0, el_1.rangeOfELs)(this.title);
        }
        else {
            const pos = this.controlsEndPos;
            return (pos !== null) ? [pos, pos] : null;
        }
    }
    get relatedArticleNumRange() {
        if (this.relatedArticleNum.length > 0) {
            return (0, el_1.rangeOfELs)(this.relatedArticleNum);
        }
        else {
            const base = this.titleRange;
            if (!base)
                return null;
            return [base[1], base[1]];
        }
    }
    rangeTexts() {
        const ret = [];
        ret.push(...this.indentRangeTexts());
        ret.push(...this.controlsRangeTexts());
        ret.push([this.titleRange, (0, _sentenceChildren_1.sentenceChildrenToString)(this.title), "Title"]);
        ret.push([this.relatedArticleNumRange, (0, _sentenceChildren_1.sentenceChildrenToString)(this.relatedArticleNum), "RelatedArticleNum"]);
        ret.push([this.lineEndTextRange(), this.lineEndText, "LineEnd"]);
        return ret;
    }
}
exports.SupplProvisionAppdxItemHeadLine = SupplProvisionAppdxItemHeadLine;
/* eslint-disable tsdoc/syntax */
/**
 * A first line of an article. Please see the source code of {@link $articleLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class ArticleLine extends IndentsLine {
    title;
    midSpace;
    sentencesArray;
    constructor(options) {
        super({ ...options, type: LineType.ART });
        this.title = options.title;
        this.midSpace = options.midSpace;
        this.sentencesArray = options.sentencesArray;
    }
    get titleRange() {
        const pos = this.indentsEndPos;
        return (pos !== null) ? [pos, pos + this.title.length] : null;
    }
    get midSpaceRange() {
        const base = this.titleRange;
        if (!base)
            return null;
        return [base[1], base[1] + this.midSpace.length];
    }
    get sentencesArrayRange() {
        if (this.sentencesArray.length > 0) {
            const ranges = this.sentencesArray.flat().map(ss => [
                ss.leadingSpaceRange,
                ...ss.attrEntries.map(e => [e.entryRange, e.trailingSpaceRange]).flat(),
                ...ss.sentences.map(s => s.range),
            ]).flat().filter(r => r !== null);
            if (ranges.length === 0)
                return null;
            return [ranges[0][0], ranges[ranges.length - 1][1]];
        }
        else {
            const base = this.midSpaceRange;
            if (!base)
                return null;
            return [base[1], base[1]];
        }
    }
    rangeTexts() {
        const ret = [];
        ret.push(...this.indentRangeTexts());
        ret.push([this.titleRange, this.title, "Title"]);
        ret.push([this.midSpaceRange, this.midSpace, "MidSpace"]);
        ret.push([this.sentencesArrayRange, (0, _sentencesArray_1.sentencesArrayToString)(this.sentencesArray), "SentencesArray"]);
        ret.push([this.lineEndTextRange(), this.lineEndText, "LineEnd"]);
        return ret;
    }
}
exports.ArticleLine = ArticleLine;
/* eslint-disable tsdoc/syntax */
/**
 * A first line of a paragraph, item, and subitem. Please see the source code of {@link $paragraphItemLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class ParagraphItemLine extends WithControlsLine {
    mainTag;
    controls;
    title;
    midSpace;
    sentencesArray;
    constructor(options) {
        super({ ...options, type: LineType.PIT });
        this.mainTag = options.mainTag;
        this.controls = options.controls;
        this.title = options.title;
        this.midSpace = options.midSpace;
        this.sentencesArray = options.sentencesArray;
    }
    get titleRange() {
        const pos = this.controlsEndPos;
        return (pos !== null) ? [pos, pos + this.title.length] : null;
    }
    get midSpaceRange() {
        const base = this.titleRange;
        if (!base)
            return null;
        return [base[1], base[1] + this.midSpace.length];
    }
    get sentencesArrayRange() {
        if (this.sentencesArray.length > 0) {
            const ranges = this.sentencesArray.flat().map(ss => [
                ss.leadingSpaceRange,
                ...ss.attrEntries.map(e => [e.entryRange, e.trailingSpaceRange]).flat(),
                ...ss.sentences.map(s => s.range),
            ]).flat().filter(r => r !== null);
            if (ranges.length === 0)
                return null;
            return [ranges[0][0], ranges[ranges.length - 1][1]];
        }
        else {
            const base = this.midSpaceRange;
            if (!base)
                return null;
            return [base[1], base[1]];
        }
    }
    rangeTexts() {
        const ret = [];
        ret.push(...this.indentRangeTexts());
        ret.push(...this.controlsRangeTexts());
        ret.push([this.titleRange, this.title, "Title"]);
        ret.push([this.midSpaceRange, this.midSpace, "MidSpace"]);
        ret.push([this.sentencesArrayRange, (0, _sentencesArray_1.sentencesArrayToString)(this.sentencesArray), "SentencesArray"]);
        ret.push([this.lineEndTextRange(), this.lineEndText, "LineEnd"]);
        return ret;
    }
    withTag(tag) {
        return new ParagraphItemLine({ ...this, mainTag: tag });
    }
}
exports.ParagraphItemLine = ParagraphItemLine;
/* eslint-disable tsdoc/syntax */
/**
 * A line of table column. Please see the source code of {@link $tableColumnLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class TableColumnLine extends IndentsLine {
    firstColumnIndicator;
    midIndicatorsSpace;
    columnIndicator;
    midSpace;
    attrEntries;
    multilineIndicator;
    sentencesArray;
    constructor(options) {
        super({ ...options, type: LineType.TBL });
        this.firstColumnIndicator = options.firstColumnIndicator;
        this.midIndicatorsSpace = options.midIndicatorsSpace;
        this.columnIndicator = options.columnIndicator;
        this.midSpace = options.midSpace;
        this.attrEntries = options.attrEntries;
        this.multilineIndicator = options.multilineIndicator;
        this.sentencesArray = options.sentencesArray;
    }
    get firstColumnIndicatorRange() {
        const pos = this.indentsEndPos;
        return (pos !== null) ? [pos, pos + this.firstColumnIndicator.length] : null;
    }
    get midIndicatorsSpaceRange() {
        const base = this.firstColumnIndicatorRange;
        if (!base)
            return null;
        return [base[1], base[1] + this.midIndicatorsSpace.length];
    }
    get columnIndicatorRange() {
        const base = this.midIndicatorsSpaceRange;
        if (!base)
            return null;
        return [base[1], base[1] + this.columnIndicator.length];
    }
    get midSpaceRange() {
        const base = this.columnIndicatorRange;
        if (!base)
            return null;
        return [base[1], base[1] + this.midSpace.length];
    }
    attrEntriesRangeTexts() {
        const ret = [];
        for (const attrEntry of this.attrEntries) {
            ret.push([
                attrEntry.entryRange,
                attrEntry.entryText,
                "AttrEntry",
            ]);
            ret.push([
                attrEntry.trailingSpaceRange,
                attrEntry.trailingSpace,
                "AttrEntryTrailingSpace",
            ]);
        }
        return ret;
    }
    get multilineIndicatorRange() {
        const attrEntriesRanges = this.attrEntriesRangeTexts().map(r => r[0]);
        const base = (attrEntriesRanges.length > 0
            ? attrEntriesRanges[attrEntriesRanges.length - 1]
            : this.midSpaceRange) ?? null;
        if (!base)
            return null;
        return [base[1], base[1] + this.multilineIndicator.length];
    }
    get sentencesArrayRange() {
        if (this.sentencesArray.length > 0) {
            const ranges = this.sentencesArray.flat().map(ss => [
                ss.leadingSpaceRange,
                ...ss.attrEntries.map(e => [e.entryRange, e.trailingSpaceRange]).flat(),
                ...ss.sentences.map(s => s.range),
            ]).flat().filter(r => r !== null);
            if (ranges.length === 0)
                return null;
            return [ranges[0][0], ranges[ranges.length - 1][1]];
        }
        else {
            const base = this.multilineIndicatorRange;
            if (!base)
                return null;
            return [base[1], base[1]];
        }
    }
    rangeTexts() {
        const ret = [];
        ret.push(...this.indentRangeTexts());
        ret.push([this.firstColumnIndicatorRange, this.firstColumnIndicator, "FirstColumnIndicator"]);
        ret.push([this.midIndicatorsSpaceRange, this.midIndicatorsSpace, "MidIndicatorsSpace"]);
        ret.push([this.columnIndicatorRange, this.columnIndicator, "ColumnIndicator"]);
        ret.push([this.midSpaceRange, this.midSpace, "MidSpace"]);
        ret.push(...this.attrEntriesRangeTexts());
        ret.push([this.multilineIndicatorRange, this.multilineIndicator, "MultilineIndicator"]);
        ret.push([this.sentencesArrayRange, (0, _sentencesArray_1.sentencesArrayToString)(this.sentencesArray, { escapeLeadingSpaces: true }), "SentencesArray"]);
        ret.push([this.lineEndTextRange(), this.lineEndText, "LineEnd"]);
        return ret;
    }
}
exports.TableColumnLine = TableColumnLine;
/* eslint-disable tsdoc/syntax */
/**
 * A line of other types. Please see the source code of {@link $otherLine} for the detailed syntax.
 */
/* eslint-enable tsdoc/syntax */
class OtherLine extends WithControlsLine {
    sentencesArray;
    constructor(options) {
        super({ ...options, type: LineType.OTH });
        this.sentencesArray = options.sentencesArray;
    }
    get sentencesArrayRange() {
        if (this.sentencesArray.length > 0) {
            const ranges = this.sentencesArray.flat().map(ss => [
                ss.leadingSpaceRange,
                ...ss.attrEntries.map(e => [e.entryRange, e.trailingSpaceRange]).flat(),
                ...ss.sentences.map(s => s.range),
            ]).flat().filter(r => r !== null);
            if (ranges.length === 0)
                return null;
            return [ranges[0][0], ranges[ranges.length - 1][1]];
        }
        else {
            const pos = this.controlsEndPos;
            return (pos !== null) ? [pos, pos] : null;
        }
    }
    rangeTexts() {
        const ret = [];
        ret.push(...this.indentRangeTexts());
        ret.push(...this.controlsRangeTexts());
        ret.push([this.sentencesArrayRange, (0, _sentencesArray_1.sentencesArrayToString)(this.sentencesArray), "SentencesArray"]);
        ret.push([this.lineEndTextRange(), this.lineEndText, "LineEnd"]);
        return ret;
    }
}
exports.OtherLine = OtherLine;
//# sourceMappingURL=line.js.map